import { Translator } from "../i18n/translation-key"
import { ServiceBikeType } from "./service-bike"

/**
 * Letter-based bike frame sizes. Used for bike types whose recommended rider
 * height is looked up by S/M/L rather than by a centimetre frame size.
 */
export enum BikeFrameSize {
  XS = "XS",
  S = "S",
  M = "M",
  L = "L",
  XL = "XL",
}

/**
 * Recommended rider-height range for a bike, in centimetres, plus a
 * ready-to-display Swedish sentence.
 *
 * - `mid1`–`mid2` is the "safe" range the bike fits optimally.
 * - `min`–`max` is the wider range it could still fit depending on body type.
 */
export interface RecommendedRiderHeight {
  min: number
  mid1: number
  mid2: number
  max: number
}

type CityLikeBikeType =
  | ServiceBikeType.CITY
  | ServiceBikeType.HYBRID
  | ServiceBikeType.VINTAGE
  | ServiceBikeType.FOLDABLE
  | ServiceBikeType.CRUISER

type RoadLikeBikeType =
  | ServiceBikeType.COUNTRY_ROAD
  | ServiceBikeType.GRAVEL
  | ServiceBikeType.CYCLOCROSS

type ChildBikeType = ServiceBikeType.CHILDREN_BIKE | ServiceBikeType.JUNIOR_BIKE

type SupportedRiderHeightBikeType =
  | ServiceBikeType.MOUNTAIN
  | CityLikeBikeType
  | RoadLikeBikeType
  | ServiceBikeType.BALANCE_BIKE
  | ChildBikeType

/** Bike types with no rider-height recommendation — always resolve to `null`. */
export type UnsupportedRiderHeightBikeType = Exclude<
  ServiceBikeType,
  SupportedRiderHeightBikeType
>

/**
 * Input for {@link getRecommendedRiderHeight}. Every `ServiceBikeType` is
 * representable: supported types require their size field, while unsupported
 * types take no size and always resolve to `null`.
 *
 * - Mountainbikes use a letter frame size only.
 * - City-like bikes accept a letter frame size or a centimetre frame size.
 * - Road-like bikes use a centimetre frame size only.
 * - Balance bikes carry no size.
 * - Children/junior bikes use a wheel size in inches.
 */
export type RecommendedRiderHeightInput =
  | { type: ServiceBikeType.MOUNTAIN; frameSize: BikeFrameSize }
  | { type: CityLikeBikeType; frameSize: BikeFrameSize | number }
  | { type: RoadLikeBikeType; frameSize: number }
  | { type: ServiceBikeType.BALANCE_BIKE }
  | { type: ChildBikeType; wheelSize: number }
  | { type: UnsupportedRiderHeightBikeType }

/** `[min, mid1, mid2, max]` in centimetres. */
type RiderHeightTuple = readonly [number, number, number, number]

/** `[size, heights]` row — `size` is centimetres (frame) or inches (wheel). */
type SizedRow = readonly [number, RiderHeightTuple]

/** Letter frame size → rider height for city-like bikes. */
const CITY_LIKE_BY_FRAME_SIZE: Record<BikeFrameSize, RiderHeightTuple> = {
  [BikeFrameSize.XS]: [145, 148, 152, 155],
  [BikeFrameSize.S]: [150, 155, 165, 174],
  [BikeFrameSize.M]: [165, 170, 180, 183],
  [BikeFrameSize.L]: [172, 175, 185, 190],
  [BikeFrameSize.XL]: [185, 187, 195, 200],
}

/** Centimetre frame size → rider height for city-like bikes (rows 43–64 cm). */
const CITY_LIKE_BY_FRAME_SIZE_CM: readonly [SizedRow, ...SizedRow[]] = [
  [43, [143, 145, 160, 165]],
  [44, [143, 145, 160, 165]],
  [45, [143, 145, 160, 165]],
  [46, [143, 145, 160, 165]],
  [47, [145, 150, 160, 165]],
  [48, [145, 150, 160, 165]],
  [49, [145, 150, 160, 165]],
  [50, [145, 150, 160, 165]],
  [51, [155, 160, 175, 180]],
  [52, [155, 160, 175, 180]],
  [53, [155, 160, 175, 180]],
  [54, [155, 160, 175, 180]],
  [55, [170, 175, 185, 190]],
  [56, [170, 175, 185, 190]],
  [57, [170, 175, 185, 191]],
  [58, [170, 175, 185, 191]],
  [59, [183, 185, 205, 207]],
  [60, [183, 185, 205, 207]],
  [61, [183, 185, 205, 207]],
  [62, [188, 190, 210, 215]],
  [63, [188, 190, 210, 215]],
  [64, [188, 190, 210, 215]],
]

/** Letter frame size → rider height for mountainbikes. */
const MOUNTAIN_BY_FRAME_SIZE: Record<BikeFrameSize, RiderHeightTuple> = {
  [BikeFrameSize.XS]: [140, 145, 160, 165],
  [BikeFrameSize.S]: [150, 155, 170, 174],
  [BikeFrameSize.M]: [165, 170, 180, 183],
  [BikeFrameSize.L]: [177, 180, 190, 192],
  [BikeFrameSize.XL]: [187, 190, 200, 205],
}

/** Centimetre frame size → rider height for road-like bikes (rows 43–64 cm). */
const ROAD_LIKE_BY_FRAME_SIZE_CM: readonly [SizedRow, ...SizedRow[]] = [
  [43, [140, 143, 153, 155]],
  [44, [140, 143, 153, 155]],
  [45, [140, 143, 153, 155]],
  [46, [143, 145, 155, 160]],
  [47, [143, 145, 155, 160]],
  [48, [155, 160, 169, 170]],
  [49, [155, 160, 169, 170]],
  [50, [165, 167, 174, 176]],
  [51, [165, 167, 174, 176]],
  [52, [163, 167, 175, 177]],
  [53, [163, 167, 175, 177]],
  [54, [170, 175, 178, 182]],
  [55, [170, 175, 178, 182]],
  [56, [178, 180, 185, 187]],
  [57, [178, 180, 185, 187]],
  [58, [183, 185, 190, 192]],
  [59, [183, 185, 190, 192]],
  [60, [188, 190, 195, 198]],
  [61, [188, 190, 195, 198]],
  [62, [188, 190, 195, 198]],
  [63, [193, 195, 200, 205]],
  [64, [193, 195, 200, 205]],
]

/** Wheel size (inches) → rider height for children/junior bikes. */
const CHILD_BY_WHEEL_SIZE: readonly [SizedRow, ...SizedRow[]] = [
  [12, [90, 95, 110, 115]],
  [14, [95, 100, 120, 125]],
  [16, [105, 110, 120, 125]],
  [18, [105, 110, 120, 125]],
  [20, [115, 120, 130, 135]],
  [24, [125, 130, 150, 155]],
  [26, [140, 145, 160, 165]],
]

/** Balance bikes have no relevant size — a single fixed range applies. */
const BALANCE_BIKE_HEIGHT: RiderHeightTuple = [80, 90, 105, 110]

const isLetterFrameSize = (
  frameSize: BikeFrameSize | number,
): frameSize is BikeFrameSize => typeof frameSize === "string"

/**
 * Returns the heights of the row whose size is closest to `target`. Out-of-range
 * values clamp to the nearest end; in-between values round to the nearest row.
 */
const nearestRow = (
  rows: readonly [SizedRow, ...SizedRow[]],
  target: number,
): RiderHeightTuple => {
  let closest = rows[0]
  for (const row of rows) {
    if (Math.abs(row[0] - target) < Math.abs(closest[0] - target)) {
      closest = row
    }
  }
  return closest[1]
}

/** The sentence that explains a recommendation to the customer. */
export const formatRiderHeightRecommendation = (
  { min, mid1, mid2, max }: RecommendedRiderHeight,
  t: Translator,
): string => t("common.riderHeight.recommendation", { min, mid1, mid2, max })

const toRecommendation = (tuple: RiderHeightTuple): RecommendedRiderHeight => {
  const [min, mid1, mid2, max] = tuple
  return { min, mid1, mid2, max }
}

/**
 * Resolves a letter frame size against a lookup table, or `null` if the size
 * is missing from the table (e.g. an out-of-enum value from untyped data).
 */
const letterRecommendation = (
  table: Record<BikeFrameSize, RiderHeightTuple>,
  frameSize: BikeFrameSize,
): RecommendedRiderHeight | null => {
  const tuple = table[frameSize]
  return tuple ? toRecommendation(tuple) : null
}

/**
 * Resolves a numeric size against a lookup table, or `null` if the size is not
 * a finite number (e.g. a malformed `NaN`/`Infinity` from a form).
 */
const numericRecommendation = (
  rows: readonly [SizedRow, ...SizedRow[]],
  size: number,
): RecommendedRiderHeight | null =>
  Number.isFinite(size) ? toRecommendation(nearestRow(rows, size)) : null

/**
 * Returns the recommended rider-height range for a bike, or `null` for bike
 * types that have no recommendation.
 *
 * Frame sizes are in centimetres, wheel sizes in inches. Numeric sizes that
 * fall between or outside the lookup rows are matched to the nearest row;
 * a non-finite numeric size (`NaN`/`Infinity`) returns `null`.
 */
export function getRecommendedRiderHeight(
  input: RecommendedRiderHeightInput,
): RecommendedRiderHeight | null {
  switch (input.type) {
    case ServiceBikeType.MOUNTAIN:
      return letterRecommendation(MOUNTAIN_BY_FRAME_SIZE, input.frameSize)
    case ServiceBikeType.CITY:
    case ServiceBikeType.HYBRID:
    case ServiceBikeType.VINTAGE:
    case ServiceBikeType.FOLDABLE:
    case ServiceBikeType.CRUISER:
      return isLetterFrameSize(input.frameSize)
        ? letterRecommendation(CITY_LIKE_BY_FRAME_SIZE, input.frameSize)
        : numericRecommendation(CITY_LIKE_BY_FRAME_SIZE_CM, input.frameSize)
    case ServiceBikeType.COUNTRY_ROAD:
    case ServiceBikeType.GRAVEL:
    case ServiceBikeType.CYCLOCROSS:
      return numericRecommendation(ROAD_LIKE_BY_FRAME_SIZE_CM, input.frameSize)
    case ServiceBikeType.BALANCE_BIKE:
      return toRecommendation(BALANCE_BIKE_HEIGHT)
    case ServiceBikeType.CHILDREN_BIKE:
    case ServiceBikeType.JUNIOR_BIKE:
      return numericRecommendation(CHILD_BY_WHEEL_SIZE, input.wheelSize)
    default:
      input satisfies { type: UnsupportedRiderHeightBikeType }
      return null
  }
}

/**
 * Which frame-size input(s) make sense when a mechanic enters a bike's size:
 *
 * - `"letter"` — mountainbikes use a letter frame size (XS–XL) only.
 * - `"cm"` — road-like bikes (landsväg/gravel/cyclocross) use a centimetre
 *   frame size only.
 * - `"both"` — city-like and every other type accept either a letter size or a
 *   centimetre size.
 *
 * Shared by the listing form (to decide which inputs to render) and the backend
 * (to validate the submitted size), so the rule lives in one place.
 */
export function getFrameSizeInputMode(
  bikeType: ServiceBikeType,
): "letter" | "cm" | "both" {
  switch (bikeType) {
    case ServiceBikeType.MOUNTAIN:
      return "letter"
    case ServiceBikeType.COUNTRY_ROAD:
    case ServiceBikeType.GRAVEL:
    case ServiceBikeType.CYCLOCROSS:
      return "cm"
    default:
      return "both"
  }
}

/**
 * Stored bike sizes are loose and nullable, so this resolves the recommendation
 * from whatever is on record — selecting the right size field per bike type and
 * returning `null` when the size needed for that type is missing.
 *
 * For city-like bikes a letter size is preferred over a centimetre size when
 * both are present.
 */
export function getRecommendedRiderHeightForBike(input: {
  bikeType: ServiceBikeType
  frameSizeLetter: BikeFrameSize | null
  frameSizeCm: number | null
  wheelSizeInches: number | null
}): RecommendedRiderHeight | null {
  const { bikeType, frameSizeLetter, frameSizeCm, wheelSizeInches } = input
  switch (bikeType) {
    case ServiceBikeType.MOUNTAIN:
      return frameSizeLetter === null
        ? null
        : getRecommendedRiderHeight({
            type: bikeType,
            frameSize: frameSizeLetter,
          })
    case ServiceBikeType.CITY:
    case ServiceBikeType.HYBRID:
    case ServiceBikeType.VINTAGE:
    case ServiceBikeType.FOLDABLE:
    case ServiceBikeType.CRUISER: {
      const frameSize = frameSizeLetter ?? frameSizeCm
      return frameSize === null
        ? null
        : getRecommendedRiderHeight({ type: bikeType, frameSize })
    }
    case ServiceBikeType.COUNTRY_ROAD:
    case ServiceBikeType.GRAVEL:
    case ServiceBikeType.CYCLOCROSS:
      return frameSizeCm === null
        ? null
        : getRecommendedRiderHeight({ type: bikeType, frameSize: frameSizeCm })
    case ServiceBikeType.BALANCE_BIKE:
      return getRecommendedRiderHeight({ type: bikeType })
    case ServiceBikeType.CHILDREN_BIKE:
    case ServiceBikeType.JUNIOR_BIKE:
      return wheelSizeInches === null
        ? null
        : getRecommendedRiderHeight({
            type: bikeType,
            wheelSize: wheelSizeInches,
          })
    default:
      bikeType satisfies UnsupportedRiderHeightBikeType
      return getRecommendedRiderHeight({ type: bikeType })
  }
}
